import { supabase } from '../utils/supabase';
import { getPasswordResetRedirectUrl, getEmailConfirmationRedirectUrl } from '../utils/redirectUrls';
import rateLimiter, { createRateLimitedCall } from '../utils/rateLimiter';
import { fetchClubDefaultStartingElo } from './guestService';
import { mapPasswordResetRequestError } from '../utils/passwordReset';

/**
 * Register a new user using the Edge Function
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} - The registered user data
 */
export const registerUser = async (userData) => {
  try {
    // Check rate limit for auth operations
    if (!rateLimiter.isAllowed('auth')) {
      const resetTime = rateLimiter.getTimeUntilReset('auth');
      throw new Error(`Too many registration attempts. Please wait ${Math.ceil(resetTime / 1000)} seconds before trying again.`);
    }

    const { role: _ignoredRole, ...registrationPayload } = userData || {};

    console.log('[AUTH] Registering user with Edge Function:', registrationPayload);
    
    // Call the Edge Function to register the user
    const { data, error } = await supabase.functions.invoke('register-user', {
      body: registrationPayload
    });

    if (error) {
      console.error('[AUTH] Error calling register-user Edge Function:', error);
      console.log('[AUTH] Falling back to direct registration');
      
      // Fallback to direct registration if Edge Function fails
      return registerUserFallback(userData);
    }

    console.log('[AUTH] User registered successfully via Edge Function:', data);
    
    // Check for guest account matches and create notifications
    if (data.user?.id && data.user?.email) {
      try {
        await supabase.rpc('check_and_create_guest_notifications', {
          p_user_id: data.user.id,
          p_email: data.user.email
        });
      } catch (err) {
        console.error('[AUTH] Error checking for guest matches:', err);
        // Don't fail registration if guest check fails
      }
    }
    
    return data.user;
  } catch (error) {
    console.error('[AUTH] Error in registerUser:', error);
    console.log('[AUTH] Falling back to direct registration');
    
    // Fallback to direct registration if Edge Function fails
    return registerUserFallback(userData);
  }
};

/**
 * Fallback function to register a user using direct database calls
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} - The registered user data
 */
const registerUserFallback = async (userData) => {
  try {
    console.log('[AUTH] Using fallback method to register user');
    
    // Step 1: Create the auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          gender: userData.gender,
        },
        emailRedirectTo: getEmailConfirmationRedirectUrl()
      }
    });

    if (authError) {
      console.error('[AUTH] Error creating auth user:', authError);
      throw authError;
    }

    if (!authData || !authData.user || !authData.user.id) {
      throw new Error('Auth user creation failed with no error');
    }

    // Step 2: Create the player record
    const { data: player, error: playerError } = await supabase
      .from('players')
      .insert([
        {
          auth_id: authData.user.id,
          email: userData.email.toLowerCase().trim(),
          first_name: userData.firstName,
          last_name: userData.lastName,
          gender: userData.gender,
          role: 'member',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (playerError) {
      console.error('[AUTH] Error creating player record:', playerError);
      throw playerError;
    }

    // Step 3: Create player-club association (only if clubId is provided)
    if (userData.clubId) {
      const { error: clubError } = await supabase
        .from('players_club')
        .insert([
          {
            player_id: player.id,
            club_id: userData.clubId,
            is_active: true,
            club_role: 'member',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ]);

      if (clubError) {
        console.error('[AUTH] Error creating player-club association:', clubError);
        // We don't need to throw here as the player and auth user are still valid
      }

      // Step 4: Initialize ELO ratings for the player in this club
      const startingElo = await fetchClubDefaultStartingElo(userData.clubId);
      const { error: eloError } = await supabase
        .from('player_club_elo_ratings')
        .insert([
          {
            player_id: player.id,
            club_id: userData.clubId,
            mixed_elo_rating: startingElo,
            levels_elo_rating: startingElo,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ]);

      if (eloError) {
        console.error('[AUTH] Error initializing player ELO ratings:', eloError);
        // We don't need to throw here as the player and auth user are still valid
      }
    }

    console.log('[AUTH] User registered successfully via fallback:', player);
    
    // Check for guest account matches and create notifications
    if (player.id && userData.email) {
      try {
        await supabase.rpc('check_and_create_guest_notifications', {
          p_user_id: player.id,
          p_email: userData.email.toLowerCase().trim()
        });
      } catch (err) {
        console.error('[AUTH] Error checking for guest matches:', err);
        // Don't fail registration if guest check fails
      }
    }
    
    return {
      id: player.id,
      auth_id: authData.user.id,
      email: userData.email,
      first_name: userData.firstName,
      last_name: userData.lastName,
      gender: userData.gender,
      role: 'member'
    };
  } catch (error) {
    console.error('[AUTH] Error in registerUserFallback:', error);
    throw error;
  }
};

/**
 * Sign in a user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} - The signed-in user data
 */
export const signIn = async (email, password) => {
  try {
    // Check rate limit for auth operations
    if (!rateLimiter.isAllowed('auth')) {
      const resetTime = rateLimiter.getTimeUntilReset('auth');
      throw new Error(`Too many login attempts. Please wait ${Math.ceil(resetTime / 1000)} seconds before trying again.`);
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get the current user session
 * @returns {Promise<Object>} - The current session
 */
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  } catch (error) {
    console.error('Error getting session:', error);
    throw error;
  }
};

/**
 * Get the current user
 * @returns {Promise<Object>} - The current user
 */
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data.user;
  } catch (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
};

/**
 * Request a password-reset email. Does not reveal whether the address exists.
 * @param {string} email - User email
 * @param {{ captchaToken?: string }} [options]
 * @returns {Promise<void>}
 */
export const resetPassword = async (email, { captchaToken } = {}) => {
  if (!rateLimiter.isAllowed('auth')) {
    const resetTime = rateLimiter.getTimeUntilReset('auth');
    throw new Error(
      `Too many reset requests. Please wait ${Math.ceil(resetTime / 1000)} seconds before trying again.`
    );
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: getPasswordResetRedirectUrl(),
    ...(captchaToken ? { captchaToken } : {}),
  });

  if (error) {
    console.error('Error resetting password:', error);
    throw new Error(mapPasswordResetRequestError(error));
  }
};

/**
 * Update password
 * @param {string} password - New password
 * @returns {Promise<void>}
 */
export const updatePassword = async (password) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password,
    });
    if (error) throw error;
  } catch (error) {
    console.error('Error updating password:', error);
    throw error;
  }
};
